import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import useAxios from '../utils/useAxios';

/** A customer with their card's page (api/clients/, api/loyalty/customers/). */
export type CardHolder = {
  name?: string | null; phone?: string | null; phone_e164?: string | null; card_path?: string | null;
  marketing_opt_in?: boolean;
};

/** The business's name, for the message (one request per page, not per row). */
export const useBusinessName = () => {
  const axios = useAxios();
  const [name, setName] = React.useState('');
  React.useEffect(() => {
    axios.get('management/organization/').then(({ data }) => setName(data?.name ?? '')).catch(() => {});
  }, [axios]);
  return name;
};

const cardUrl = (holder: CardHolder) => (holder.card_path ? `${window.location.origin}${holder.card_path}` : '');

/** Opened from here to look at it: the card page doesn't keep it on this browser as the card of whoever orders here. */
const lookUrl = (holder: CardHolder) => `${holder.card_path}?vista=1`;

const whatsappUrl = (holder: CardHolder, business: string) => {
  const digits = (holder.phone_e164 || holder.phone || '').replace(/\D/g, '');
  if (!digits || !holder.card_path) return null;
  const first = (holder.name ?? '').trim().split(' ')[0];
  const text = `Hola${first ? ` ${first}` : ''} 👋 Esta es tu tarjeta de cliente${business ? ` de ${business}` : ''}: ${cardUrl(holder)}\n`
    + 'Guárdala para ver tus beneficios y usarlos en tus compras.';
  return `https://wa.me/${digits.length === 10 ? `52${digits}` : digits}?text=${encodeURIComponent(text)}`;
};

const useCopy = (holder: CardHolder) => {
  const [copied, setCopied] = React.useState<'' | 'ok' | 'failed'>('');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl(holder));
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
    window.setTimeout(() => setCopied(''), 2000);
  };
  return { copied, copy };
};

const stop = (e: React.MouseEvent) => e.stopPropagation();

/** In a table's row: send the customer their card by WhatsApp, copy its link, look at it. */
export const CardLinkButtons = ({ holder, business }: { holder: CardHolder; business: string }) => {
  const { copied, copy } = useCopy(holder);
  if (!holder.card_path) return null;
  const whatsapp = whatsappUrl(holder, business);
  return (
    <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }} onClick={stop}>
      <Tooltip title={!whatsapp ? 'Sin teléfono' : holder.marketing_opt_in === false
        ? 'Enviarle su tarjeta por WhatsApp (no aceptó promociones: solo su tarjeta, sin ofertas)' : 'Enviarle su tarjeta por WhatsApp'}>
        <span>
          <IconButton size="small" disabled={!whatsapp} href={whatsapp ?? ''} target="_blank" rel="noopener" aria-label="Enviar su tarjeta por WhatsApp" data-testid="card-whatsapp">
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={copied === 'ok' ? '¡Enlace copiado!' : copied === 'failed' ? 'No se pudo copiar' : 'Copiar el enlace de su tarjeta'}>
        <IconButton size="small" onClick={copy} aria-label="Copiar el enlace de su tarjeta" data-testid="card-copy">
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ver su tarjeta">
        <IconButton size="small" href={lookUrl(holder)} target="_blank" rel="noopener" aria-label="Ver su tarjeta">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

/** On the customer's page: their card's link, to send or copy. */
export const CardLinkSection = ({ holder, business }: { holder: CardHolder; business: string }) => {
  const { copied, copy } = useCopy(holder);
  if (!holder.card_path) return null;
  const whatsapp = whatsappUrl(holder, business);
  return (
    <Box data-testid="card-link">
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Sus recompensas y su saldo, en su teléfono. Es personal: envíasela solo a este cliente.
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" variant="contained" startIcon={<WhatsAppIcon />} disabled={!whatsapp} href={whatsapp ?? ''} target="_blank" rel="noopener" data-testid="card-whatsapp">
          Enviar por WhatsApp
        </Button>
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy} data-testid="card-copy">
          {copied === 'ok' ? '¡Copiado!' : copied === 'failed' ? 'No se pudo copiar' : 'Copiar enlace'}
        </Button>
        <Button size="small" startIcon={<OpenInNewIcon />} href={lookUrl(holder)} target="_blank" rel="noopener">Ver</Button>
      </Stack>
      {!whatsapp && <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 1 }}>Agrega su teléfono para enviársela por WhatsApp.</Typography>}
      {whatsapp && holder.marketing_opt_in === false && (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 1 }}>No aceptó promociones: envíale solo su tarjeta, sin ofertas.</Typography>
      )}
    </Box>
  );
};
